/**
 * The meeting roster as Phoenix Presence describes it — and the ORDER people
 * appear in, which Presence does not describe at all.
 *
 * Grid order is seat order: the order the roster Map was built in. That order
 * has to be a fact about when someone arrived and nothing else, or tiles move
 * for reasons the viewer cannot see:
 *
 *  - Presence reports a state change (camera on, mic muted, hand raised) as a
 *    `presence_diff` that LEAVES the user's old meta and JOINS the new one. Read
 *    as "remove, then add", that sends the person to the back of the room every
 *    time they touch a control. Here the metas are reconciled per key (by
 *    `phx_ref`), so a key that survives the diff keeps its seat.
 *  - A `presence_state` snapshot arrives on every (re)join, in whatever order
 *    the server's map happens to serialise — Elixir sorts small maps by key, so
 *    effectively by user id. People already seated keep their seats; only
 *    newcomers are placed, in order of `joined_at`.
 *
 * Pure functions over immutable Maps so the ordering rules are testable and
 * each call returns the previous Map when nothing changed.
 */
import { isShareOnlyPeer } from './share-only'

export type PresenceMeta = {
  phx_ref?: string
  user_name?: string
  video_on?: boolean
  status?: string
  hand_raised?: boolean
  /** Set by the server from the peer's own `media:mic_on` / `media:mic_off`. */
  audio_muted?: boolean
  /** ISO timestamp set by the server when the user first tracked into the room. */
  joined_at?: string
  [key: string]: unknown
}

export type PresenceEntry = { metas?: PresenceMeta[] }

/** userId -> that user's live metas (one per connection), in seat order. */
export type PresenceRoster = ReadonlyMap<string, readonly PresenceMeta[]>

export type PresenceDiff = {
  joins?: Record<string, PresenceEntry>
  leaves?: Record<string, PresenceEntry>
}

/** What the rest of the client needs to know about someone in the room. */
export type PeerPresence = {
  /** Display name from the Phoenix meta (`user_name`). */
  name: string
  /** Peer's self-reported camera intent. Rendering still waits for real media. */
  videoOn: boolean
  /** online / away / busy / dnd. */
  status: string
  /**
   * Hand up right now.
   *
   * Carried in Presence rather than as a one-off broadcast because it is state:
   * someone joining late has to see the hands already raised, and Presence
   * replays its metas on join.
   */
  handRaised: boolean
  /**
   * The peer's microphone and camera as the PEER reports them, or null when its
   * client does not report (older clients, until they send their first state).
   *
   * This is what the Participants panel shows. What a viewer receives is a
   * different question: a camera whose tile is off-screen is paused for that
   * viewer, a dip in bandwidth starves it, a reconnect leaves no consumer at
   * all for a few seconds — and reading the panel off received media turned
   * every one of those into "camera off" or "mic off" for someone whose camera
   * and mic were on.
   *
   * `audio_muted` is only ever written by `media:mic_on`/`mic_off`, so its
   * presence is what says the client reports; `video_on` alone cannot, because
   * the server seeds it `false` for everyone at join.
   */
  media: { micOn: boolean; camOn: boolean } | null
  /**
   * In the meeting as a screen only, from the dashboard's Screen Share
   * (`isShareOnlyPeer`): no tile, listed once in the People panel. Known from
   * the first meta — the server sets `share_only` when it tracks the session —
   * so no viewer draws a tile it has to take back.
   */
  shareOnly: boolean
}

export const EMPTY_ROSTER: PresenceRoster = new Map()

function joinedAtMs(metas: readonly PresenceMeta[] | undefined): number {
  let earliest = Number.POSITIVE_INFINITY
  for (const m of metas ?? []) {
    const t = typeof m.joined_at === 'string' ? Date.parse(m.joined_at) : Number.NaN
    if (!Number.isNaN(t) && t < earliest) earliest = t
  }
  return earliest
}

/** Newcomers in arrival order; ties (or no timestamp) fall back to id, so every client agrees. */
function byArrival(entries: [string, readonly PresenceMeta[]][]): [string, readonly PresenceMeta[]][] {
  return entries
    .map((entry) => ({ entry, at: joinedAtMs(entry[1]) }))
    .sort((a, b) => (a.at !== b.at ? a.at - b.at : a.entry[0] < b.entry[0] ? -1 : a.entry[0] > b.entry[0] ? 1 : 0))
    .map(({ entry }) => entry)
}

function sameMetas(a: readonly PresenceMeta[], b: readonly PresenceMeta[]): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    if (a[i].phx_ref === undefined || a[i].phx_ref !== b[i].phx_ref) return false
  }
  return true
}

function sameRoster(a: PresenceRoster, b: PresenceRoster): boolean {
  if (a.size !== b.size) return false
  const ai = a.entries()
  const bi = b.entries()
  for (let i = 0; i < a.size; i++) {
    const [ak, am] = ai.next().value!
    const [bk, bm] = bi.next().value!
    if (ak !== bk || !sameMetas(am, bm)) return false
  }
  return true
}

/**
 * Apply a full `presence_state` snapshot.
 *
 * The snapshot is the truth about WHO is here; the previous roster is the
 * truth about WHERE they sit. Anyone still present keeps their place, anyone
 * absent is removed, and newcomers are appended in arrival order.
 */
export function applyPresenceState(prev: PresenceRoster, snapshot: Record<string, PresenceEntry>): PresenceRoster {
  const next = new Map<string, readonly PresenceMeta[]>()
  for (const id of prev.keys()) {
    const fresh = snapshot[id]?.metas
    if (fresh && fresh.length > 0) next.set(id, fresh)
  }
  const newcomers: [string, readonly PresenceMeta[]][] = []
  for (const [id, entry] of Object.entries(snapshot)) {
    if (!next.has(id) && entry.metas && entry.metas.length > 0) newcomers.push([id, entry.metas])
  }
  for (const [id, metas] of byArrival(newcomers)) next.set(id, metas)
  return sameRoster(prev, next) ? prev : next
}

/**
 * Apply an incremental `presence_diff`.
 *
 * Metas are reconciled per key by `phx_ref`: a leave removes the metas it
 * names, a join adds its metas. A key is dropped only when it has no metas
 * left — so an update (leave old meta + join new meta) and a second tab
 * closing both leave the person exactly where they were.
 */
export function applyPresenceDiff(prev: PresenceRoster, diff: PresenceDiff): PresenceRoster {
  const joins = diff.joins ?? {}
  const leaves = diff.leaves ?? {}
  if (Object.keys(joins).length === 0 && Object.keys(leaves).length === 0) return prev

  const next = new Map(prev)

  for (const [id, entry] of Object.entries(leaves)) {
    const current = next.get(id)
    if (!current) continue
    const leaving = entry.metas ?? []
    const refs = new Set(leaving.map((m) => m.phx_ref).filter((r): r is string => typeof r === 'string'))
    // A leave without refs cannot be matched to a connection; treat it as the
    // whole user leaving, which is what a ref-less server means by it.
    const remaining = refs.size > 0 ? current.filter((m) => !refs.has(m.phx_ref ?? '')) : []
    if (remaining.length > 0 || id in joins) next.set(id, remaining)
    else next.delete(id)
  }

  const newcomers: [string, readonly PresenceMeta[]][] = []
  for (const [id, entry] of Object.entries(joins)) {
    const joining = entry.metas ?? []
    const current = next.get(id)
    if (current) {
      // Same seat: replace any meta with the same ref, add the rest.
      const refs = new Set(joining.map((m) => m.phx_ref))
      next.set(id, [...current.filter((m) => !refs.has(m.phx_ref)), ...joining])
    } else if (joining.length > 0) {
      newcomers.push([id, joining])
    }
  }
  for (const [id, metas] of byArrival(newcomers)) next.set(id, metas)

  // A key kept only because it was also joining, but whose join carried no metas.
  for (const [id, metas] of next) if (metas.length === 0) next.delete(id)

  return sameRoster(prev, next) ? prev : next
}

/** The newest connection's meta speaks for the user. */
function toPeer(id: string, metas: readonly PresenceMeta[]): PeerPresence {
  const m = metas[metas.length - 1] ?? {}
  return {
    name: m.user_name || '',
    videoOn: !!m.video_on,
    status: m.status || 'online',
    handRaised: !!m.hand_raised,
    media: typeof m.audio_muted === 'boolean' ? { micOn: !m.audio_muted, camOn: !!m.video_on } : null,
    shareOnly: isShareOnlyPeer(id, { share_only: m.share_only }),
  }
}

function samePeer(a: PeerPresence, b: PeerPresence): boolean {
  return a.name === b.name && a.videoOn === b.videoOn && a.status === b.status && a.handRaised === b.handRaised
    && a.media?.micOn === b.media?.micOn && a.media?.camOn === b.media?.camOn
    && a.shareOnly === b.shareOnly
}

/**
 * The peer view of a roster, reusing `prev` — the whole Map, or each unchanged
 * peer object — wherever nothing the UI reads has changed.
 *
 * Order-aware: a different seat order is a different roster. Metas that change
 * without changing a visible field
 * return `prev` untouched, so they cost no render at all.
 */
export function toPeers(roster: PresenceRoster, prev: ReadonlyMap<string, PeerPresence>): ReadonlyMap<string, PeerPresence> {
  const next = new Map<string, PeerPresence>()
  let changed = roster.size !== prev.size
  const prevKeys = prev.keys()
  for (const [id, metas] of roster) {
    const peer = toPeer(id, metas)
    const before = prev.get(id)
    const reused = before && samePeer(before, peer) ? before : peer
    if (!changed && (prevKeys.next().value !== id || reused !== before)) changed = true
    next.set(id, reused)
  }
  return changed ? next : prev
}
